// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  ChevronDownIcon,
  ChevronUpIcon,
  ConfirmDialog,
  EmptyState,
  FormField,
  GhostButton,
  Input,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  StatusBadge,
  TrashIcon,
} from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import {
  useAddAdminEmailSuppression,
  useAdminEmailSuppressions,
  useRemoveAdminEmailSuppression,
} from "../../hooks/useAdminEmailSuppressions";
import { formatCount, formatDateTime, formatRelativeTime } from "../automations/automationUtils";
import { getSuppressionReasonMeta } from "./messagesUtils";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ReasonBadge = ({ reason }) => {
  const meta = getSuppressionReasonMeta(reason);
  return (
    <StatusBadge size="sm" variant={meta.tone} title={meta.description}>
      {meta.label}
    </StatusBadge>
  );
};

ReasonBadge.propTypes = { reason: PropTypes.string };

const SkeletonRows = () => (
  <div className="space-y-3" aria-busy="true" aria-live="polite">
    {[0, 1, 2].map((i) => (
      <div key={i} className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
        <div className="animate-pulse space-y-3">
          <div className={`h-4 ${surfaces.tertiary} rounded w-1/3`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-1/2`} />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading suppression list…</span>
  </div>
);

/**
 * Collapsible "Email suppression list" card: the do-not-email addresses
 * (bounces / complaints from the provider + manual entries), searchable and
 * paged, with an add form (manual entry) and per-row removal.
 */
const EmailSuppressionCard = ({ showToast, defaultOpen = false, className = "" }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const searchTimer = useRef(null);

  const [addEmail, setAddEmail] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [addError, setAddError] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);

  const listQuery = useAdminEmailSuppressions({ q, page, page_size: PAGE_SIZE, enabled: open });
  const addMutation = useAddAdminEmailSuppression();
  const removeMutation = useRemoveAdminEmailSuppression();

  const notify = (type, message) => {
    if (typeof showToast === "function") showToast(type, message);
  };

  useEffect(() => () => searchTimer.current && clearTimeout(searchTimer.current), []);

  const applySearch = (value, { immediate = false } = {}) => {
    setSearchText(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const commit = () => {
      setQ(value.trim());
      setPage(1);
    };
    if (immediate) commit();
    else searchTimer.current = setTimeout(commit, SEARCH_DEBOUNCE_MS);
  };

  const results = useMemo(() => (Array.isArray(listQuery.data?.results) ? listQuery.data.results : []), [listQuery.data]);
  const total = Number(listQuery.data?.total || 0);
  const pageSize = Number(listQuery.data?.page_size || PAGE_SIZE);
  const currentPage = Number(listQuery.data?.page || page);
  const first = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const last = total === 0 ? 0 : first + results.length - 1;
  const hasMore = last < total;

  const handleAdd = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const email = addEmail.trim().toLowerCase();
    if (!email) {
      setAddError("Enter an email address.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setAddError("That does not look like a valid email address.");
      return;
    }
    setAddError("");
    try {
      await addMutation.mutateAsync({ email, notes: addNotes.trim() });
      setAddEmail("");
      setAddNotes("");
      setPage(1);
      notify("success", `${email} will no longer receive email.`);
    } catch (err) {
      const message = err?.message || "Failed to add the address.";
      setAddError(message);
      notify("error", message);
    }
  };

  const handleRemove = async () => {
    const target = removeTarget;
    if (!target) return;
    try {
      await removeMutation.mutateAsync(target.id);
      notify("success", `Removed ${target.email} — it can receive email again.`);
    } catch (err) {
      notify("error", err?.message || "Failed to remove the address.");
    }
  };

  const subtitle = open
    ? "Addresses that never receive email — fed by bounces/complaints and manual entries. Emails to them are marked “suppressed” instead of being sent."
    : "Addresses that never receive email — fed by bounces/complaints and manual entries.";

  const isEmpty = !listQuery.isLoading && !listQuery.error && total === 0 && results.length === 0;

  return (
    <Card variant="glass" className={className}>
      <CardHeader
        title="Email suppression list"
        subtitle={subtitle}
        actions={
          <SecondaryButton
            type="button"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="email-suppression-panel"
            rightIcon={open ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
          >
            {open ? "Hide" : "Show"}
          </SecondaryButton>
        }
      />

      {open ? (
        <CardBody id="email-suppression-panel" className="mt-4 space-y-5">
          {/* Add form */}
          <form
            onSubmit={handleAdd}
            className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}
            aria-labelledby="email-suppression-add-title"
          >
            <h3 id="email-suppression-add-title" className={`${typography.h5} ${textColors.primary}`}>
              Add an address
            </h3>
            <p className={`mt-1 mb-3 ${typography.caption} ${textColors.tertiary}`}>
              Manual entries are stored lowercase; adding an address that is already listed only updates its note.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_auto] gap-3 md:items-end">
              <FormField label="Email" htmlFor="email-suppression-email" required>
                <Input
                  id="email-suppression-email"
                  type="email"
                  value={addEmail}
                  onChange={(e) => {
                    setAddEmail(e.target.value);
                    if (addError) setAddError("");
                  }}
                  placeholder="someone@example.com"
                  error={addError}
                  disabled={addMutation.isPending}
                  autoComplete="off"
                />
              </FormField>
              <FormField label="Note" htmlFor="email-suppression-notes" optional>
                <Input
                  id="email-suppression-notes"
                  type="text"
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  placeholder="Why this address is blocked"
                  disabled={addMutation.isPending}
                  maxLength={500}
                />
              </FormField>
              <PrimaryButton type="submit" loading={addMutation.isPending} className="w-full md:w-auto whitespace-nowrap">
                Add to list
              </PrimaryButton>
            </div>
          </form>

          {/* Search + refresh */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="sm:w-80">
              <SearchInput
                value={searchText}
                onChange={(e) => applySearch(e.target.value)}
                onClear={() => applySearch("", { immediate: true })}
                placeholder="Search email…"
                aria-label="Search suppressed addresses"
                size="sm"
              />
            </div>
            <div className={`${typography.caption} ${textColors.tertiary} sm:ml-auto`} aria-live="polite">
              {listQuery.isLoading ? "Loading…" : listQuery.error ? "" : `${formatCount(total)} address${total === 1 ? "" : "es"}`}
            </div>
            <SecondaryButton size="sm" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
              {listQuery.isFetching ? "Refreshing…" : "Refresh"}
            </SecondaryButton>
          </div>

          {listQuery.error ? (
            <div className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800" role="alert">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
                  Failed to load the suppression list: {listQuery.error?.message || "Unknown error"}
                </p>
                <SecondaryButton size="sm" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
                  Retry
                </SecondaryButton>
              </div>
            </div>
          ) : listQuery.isLoading ? (
            <SkeletonRows />
          ) : isEmpty ? (
            <div className={`rounded-2xl ${surfaces.secondary}`}>
              {q ? (
                <EmptyState
                  title="No addresses match"
                  description="Try another search term."
                  action={
                    <SecondaryButton size="sm" onClick={() => applySearch("", { immediate: true })}>
                      Clear search
                    </SecondaryButton>
                  }
                />
              ) : (
                <EmptyState
                  title="No suppressed addresses"
                  description="Bounces and complaints reported by the email provider land here automatically; you can also add an address by hand above."
                />
              )}
            </div>
          ) : (
            <div className={listQuery.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"} aria-busy={listQuery.isFetching}>
              {/* Mobile: cards */}
              <ol className="space-y-3 md:hidden" aria-label="Suppressed addresses">
                {results.map((row) => (
                  <li key={row.id} className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`${typography.bodySmall} ${textColors.primary} font-medium break-all`}>{row.email}</div>
                        <div className={`${typography.caption} ${textColors.tertiary}`} title={row.created_at || ""}>
                          Added {formatRelativeTime(row.created_at)} · {formatDateTime(row.created_at)}
                        </div>
                      </div>
                      <ReasonBadge reason={row.reason} />
                    </div>
                    <dl className={`mt-3 grid grid-cols-2 gap-3 pt-3 border-t ${borders.glass}`}>
                      <div>
                        <dt className={`${typography.caption} ${textColors.muted}`}>Source</dt>
                        <dd className={`${typography.bodySmall} ${textColors.primary}`}>{row.source || "—"}</dd>
                      </div>
                      <div>
                        <dt className={`${typography.caption} ${textColors.muted}`}>Notes</dt>
                        <dd className={`${typography.bodySmall} ${textColors.primary} break-words`}>{row.notes || "—"}</dd>
                      </div>
                    </dl>
                    <div className={`mt-3 pt-3 border-t ${borders.glass} flex justify-end`}>
                      <GhostButton size="sm" variant="danger" leftIcon={<TrashIcon size="sm" />} onClick={() => setRemoveTarget(row)}>
                        Remove
                      </GhostButton>
                    </div>
                  </li>
                ))}
              </ol>

              {/* Desktop: table */}
              <div className={`hidden md:block rounded-2xl border ${borders.glass} overflow-hidden`}>
                <div className={`overflow-x-auto ${surfaces.glassCard}`}>
                  <table className="min-w-full text-left" aria-label="Suppressed addresses">
                    <thead className={surfaces.glass}>
                      <tr className={`${typography.caption} ${textColors.secondary}`}>
                        <th className="px-3 py-2 font-medium">Email</th>
                        <th className="px-3 py-2 font-medium">Reason</th>
                        <th className="px-3 py-2 font-medium">Source</th>
                        <th className="px-3 py-2 font-medium">Notes</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Added</th>
                        <th className="px-3 py-2 font-medium text-right">
                          <span className="sr-only">Row actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((row) => (
                        <tr key={row.id} className={`border-t ${borders.glass} align-top ${surfaces.hover}`}>
                          <td className={`px-3 py-3 ${typography.bodySmall} ${textColors.primary} font-medium break-all min-w-[200px]`}>
                            {row.email}
                          </td>
                          <td className="px-3 py-3">
                            <ReasonBadge reason={row.reason} />
                          </td>
                          <td className={`px-3 py-3 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>{row.source || "—"}</td>
                          <td className={`px-3 py-3 ${typography.bodySmall} ${textColors.secondary} max-w-[320px] break-words`}>
                            {row.notes || <span className={textColors.muted}>—</span>}
                          </td>
                          <td className={`px-3 py-3 ${typography.bodySmall} ${textColors.primary} whitespace-nowrap`} title={row.created_at || ""}>
                            <div>{formatRelativeTime(row.created_at)}</div>
                            <div className={`${typography.caption} ${textColors.tertiary}`}>{formatDateTime(row.created_at)}</div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            <GhostButton
                              size="sm"
                              variant="danger"
                              leftIcon={<TrashIcon size="sm" />}
                              onClick={() => setRemoveTarget(row)}
                              aria-label={`Remove ${row.email} from the suppression list`}
                            >
                              Remove
                            </GhostButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className={`${typography.caption} ${textColors.tertiary}`} aria-live="polite">
                  Showing {formatCount(first)}–{formatCount(last)} of {formatCount(total)}
                </p>
                <div className="flex items-center gap-2">
                  <SecondaryButton size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1 || listQuery.isFetching}>
                    Previous
                  </SecondaryButton>
                  <span className={`${typography.caption} ${textColors.secondary} tabular-nums`}>Page {formatCount(currentPage)}</span>
                  <SecondaryButton size="sm" onClick={() => setPage((p) => p + 1)} disabled={!hasMore || listQuery.isFetching}>
                    Next
                  </SecondaryButton>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      ) : null}

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => {
          if (removeMutation.isPending) return;
          setRemoveTarget(null);
        }}
        onConfirm={handleRemove}
        variant="danger"
        title="Remove from the suppression list?"
        message={`${removeTarget?.email || "This address"} will receive email again. If the provider reports another bounce or complaint it is added back automatically.`}
        confirmText="Remove"
        cancelText="Keep it"
        isLoading={removeMutation.isPending}
        size="md"
      />
    </Card>
  );
};

EmailSuppressionCard.propTypes = {
  /** `(type, message)` — the page toast; success / error notices go through it. */
  showToast: PropTypes.func,
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
};

export default EmailSuppressionCard;
